/*
 * Build-time-only network shim.
 *
 * While react-snap prerenders the site (hundreds of pages), every page would
 * otherwise call the backend API — which trips its rate limit (HTTP 429) and
 * leaves pages half-rendered. Instead, the build writes ALL offers/blogs to a
 * single static file (`/seo-data.json`, produced by scripts/prerender.js) and
 * this shim answers the app's API calls from that file.
 *
 * It ONLY activates under react-snap's user agent, so real users in a normal
 * browser always hit the real API exactly as before.
 */

const KNOWN_STORES = ['flipkart', 'amazon', 'myntra', 'ajio'];

function jsonResponse(body, status = 200) {
  return new Response(JSON.stringify(body), {
    status,
    headers: { 'Content-Type': 'application/json' },
  });
}

// Map an API request to a response body using the prefetched data.
// Returns `undefined` if the request isn't one we can serve (caller falls
// back to the real network).
function resolveFromData(urlStr, data) {
  let url;
  try {
    url = new URL(urlStr, window.location.origin);
  } catch (_) {
    return undefined;
  }

  const apiIdx = url.pathname.indexOf('/api/');
  if (apiIdx === -1) return undefined;

  const rest = url.pathname.slice(apiIdx + '/api/'.length).replace(/\/+$/, '');
  const segs = rest.split('/').filter(Boolean).map(decodeURIComponent);
  const q = url.searchParams;

  const offersList = data.offersList || [];
  const blogsList = data.blogsList || [];

  if (segs[0] === 'featured-offers') {
    return { featuredOffers: data.featured || [] };
  }

  if (segs[0] === 'offers') {
    // /api/offers/store/<Store>
    if (segs[1] === 'store' && segs[2]) {
      const store = segs[2].toLowerCase();
      const offers =
        store === 'others'
          ? offersList.filter(o => !o.store || !KNOWN_STORES.includes(o.store.toLowerCase()))
          : offersList.filter(o => o.store && o.store.toLowerCase() === store);
      return { offers, lastEvaluatedKey: null };
    }
    // /api/offers/<Type>/<id>  → a single offer
    if (segs.length >= 3) {
      const offer = (data.offersById || {})[segs[2]];
      return offer || null;
    }
    // /api/offers  → the list (optionally filtered by category)
    const category = q.get('category');
    let list = offersList;
    if (category && category !== 'All') list = list.filter(o => o.type === category);
    return { offers: list.slice(0, 24), lastEvaluatedKey: null };
  }

  if (segs[0] === 'blogs') {
    // /api/blogs/<Category>/<id>  → a single blog
    if (segs.length >= 3) {
      const blog = (data.blogsById || {})[segs[2]];
      return blog || null;
    }
    // /api/blogs  → the list (optionally filtered by category)
    const category = q.get('category');
    let list = blogsList;
    if (category && category !== 'All') list = list.filter(b => b.category === category);
    return { blogs: list.slice(0, 24), lastEvaluatedKey: null };
  }

  return undefined;
}

export function installPrerenderFetch() {
  const isPrerender =
    typeof navigator !== 'undefined' && /ReactSnap/i.test(navigator.userAgent);
  if (!isPrerender || typeof window === 'undefined' || !window.fetch) return;

  const originalFetch = window.fetch.bind(window);
  const dataPromise = originalFetch('/seo-data.json')
    .then(r => (r.ok ? r.json() : null))
    .catch(() => null);

  window.fetch = async (input, init) => {
    const urlStr =
      typeof input === 'string' ? input : (input && input.url) || String(input);

    if (urlStr.includes('/api/')) {
      const data = await dataPromise;
      if (data) {
        const body = resolveFromData(urlStr, data);
        if (body !== undefined) {
          return jsonResponse(body, body === null ? 404 : 200);
        }
      }
    }
    return originalFetch(input, init);
  };
}
